// =============================================================================
// staleBundle — detect (and recover from) running an OUTDATED app bundle.
//
// The problem: a deploy restarts the backend, every client RECONNECTS (the
// graceful-restart flow), but nothing ever told them to RELOAD — so they keep
// running the old JS bundle indefinitely. Stale clients then exercise old code
// against a new server (e.g. missing fixes like the keyframe watchdog), and
// "works after a refresh" bugs pile up.
//
// The mechanism: genBuildInfo.js stamps the commit SHA into BOTH
//   - src/buildInfo.json  → baked into this running bundle at build time, and
//   - public/buildInfo.json → served by nginx alongside the bundle, so it always
//     reflects the build currently ON THE SERVER.
// After every successful socket (re)connect — which includes the moment right
// after a deploy — we fetch the served stamp and compare. Mismatch = this tab is
// running an older build than the server has → reload ONCE to pick it up.
//
// Safety rails:
//   - One-shot per served SHA (sessionStorage): if we already reloaded for this
//     SHA and STILL mismatch (e.g. an aggressive cache layer), we log and stop —
//     never a reload loop.
//   - 'unknown' stamps (dev without git, fetch failures, non-JSON) → do nothing.
//   - Backend-only deploys don't rebuild chat, so the served stamp is unchanged
//     and nobody reloads. Only a chat rebuild triggers it.
// =============================================================================

import buildInfo from '../buildInfo.json';

const RELOADED_KEY = 'cc-stale-reloaded-for';

/**
 * Compare this bundle's baked build stamp against the one the server is
 * currently serving; hard-reload once if the server has a newer build.
 * Call after each successful socket (re)connect. Never throws.
 *
 * @returns {Promise<'fresh'|'reloading'|'blocked'|'skipped'>} outcome, for logs.
 */
export async function checkForNewBuild() {
  const baked = buildInfo?.shaFull;
  if (!baked || baked === 'unknown') return 'skipped';

  let served;
  try {
    const res = await fetch(
      `${process.env.PUBLIC_URL || ''}/buildInfo.json?t=${Date.now()}`,
      { cache: 'no-store' }
    );
    if (!res.ok) return 'skipped';
    served = (await res.json())?.shaFull;
  } catch (e) {
    return 'skipped'; // network hiccup — never block or reload on uncertainty
  }
  if (!served || served === 'unknown') return 'skipped';

  if (served === baked) {
    // Up to date — clear any previous guard so a FUTURE deploy can reload again.
    try { sessionStorage.removeItem(RELOADED_KEY); } catch (e) { /* private mode */ }
    return 'fresh';
  }

  // Server has a different (newer) build than this tab is running.
  let alreadyTried = false;
  try {
    alreadyTried = sessionStorage.getItem(RELOADED_KEY) === served;
  } catch (e) { /* private mode — fall through, worst case one extra reload */ }
  if (alreadyTried) {
    console.warn(
      `⚠️ Bundle still stale after reload (running ${baked.slice(0, 7)}, server has ${served.slice(0, 7)}) — not looping.`
    );
    return 'blocked';
  }
  try { sessionStorage.setItem(RELOADED_KEY, served); } catch (e) { /* ignore */ }
  console.log(
    `🔄 New build on server (${served.slice(0, 7)} vs running ${baked.slice(0, 7)}) — reloading to update.`
  );
  window.location.reload();
  return 'reloading';
}
